import { supabase } from "@/integrations/supabase/client";
import { Event, EventSubmission } from "@/types";

export const fetchEvents = async () => {
  console.log("Fetching events...");
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('approved', true) // Only fetch approved events
    .gte('end_date', new Date().toISOString()) // Only fetch events that haven't ended yet
    .order('start_date', { ascending: true });
    
  if (error) {
    console.error("Error fetching events:", error);
    throw error;
  }
  
  // Validate data format for easier debugging
  if (data && data.length > 0) {
    console.log("First event from database:", data[0]);
    console.log("Total events fetched:", data.length);
  } else {
    console.log("No events found in database");
  }
  
  return data as Event[];
};

// Function to fetch all events, including unapproved ones (for admin use)
export const fetchAllEvents = async () => {
  console.log("Fetching all events (including unapproved)...");
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .order('start_date', { ascending: true });
    
  if (error) {
    console.error("Error fetching all events:", error);
    throw error;
  }
  
  return data as Event[];
};

export const fetchEventById = async (id: string) => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error) {
    console.error("Error fetching event by ID:", error);
    throw error;
  }
  
  return data as Event;
};

export const submitEvent = async (eventData: EventSubmission) => {
  console.log("Submitting event data:", eventData);
  
  // Ensure dates are properly formatted as ISO strings
  const formattedStartDate = eventData.start_date.toISOString();
  const formattedEndDate = eventData.end_date.toISOString();
  
  console.log("Formatted dates for submission:", {
    start: formattedStartDate,
    end: formattedEndDate
  });
  
  const { error, data: responseData } = await supabase
    .from('events')
    .insert([{
      title: eventData.title,
      description: eventData.description || null,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      location: eventData.location || null,
      organizer: eventData.organizer || null,
      image_url: eventData.image_url || null,
      website: eventData.website || null,
      category: eventData.category || null,
    }])
    .select();
    
  if (error) {
    console.error("Event submission error:", error);
    throw error;
  }
  
  console.log("Event submission successful. Response:", responseData);
  return responseData;
};

export const fetchEventCategories = async () => {
  console.log("Fetching event categories...");
  try {
    const { data, error } = await supabase
      .from('events')
      .select('category')
      .not('category', 'is', null)
      .eq('approved', true);
      
    if (error) {
      console.error("Error fetching event categories:", error);
      throw error;
    }
    
    // Extract unique categories and remove nulls
    const categories = Array.from(new Set(
      data
        .map(item => item.category)
        .filter((category): category is string => !!category)
    )).sort();
    
    console.log("Fetched categories:", categories);
    return categories;
  } catch (err) {
    console.error("Failed to fetch categories:", err);
    // Return an empty array as fallback to prevent component from disappearing
    return [];
  }
};

export const approveEvent = async (eventId: string) => {
  const { data, error } = await supabase
    .rpc('approve_event', { event_id: eventId });
    
  if (error) {
    console.error("Error approving event:", error);
    throw error;
  }
  
  return data;
};
